import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Dropdown } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import CardDropdown from '../../../../../components/common/CardDropdown';
import StatisticsCopyIcon from '../../../common/StatisticsCopyIcon';
import StatisticsDeleteIcon from '../../../common/StatisticsDeleteIcon';
import StatisticsWidthIcon from '../../../common/StatisticsWidthIcon';
import StatisticsHeightIcon from '../../../common/StatisticsHeightIcon';
import { StatisticsChartCardAction, StatisticsChartCardFullscreenAction, StatisticsChartCardToolbar } from '../../../common/statistic-chart-card/StatisticsChartCardActions';
import { CHART_CARD_SURFACE, getBoardChartCardClass } from '../../../common/statistic-chart-card/chartCard.js';
import { buildHomeDashboardChartPreviewSlotStyle, HOME_DASHBOARD_CHART_PREVIEW_HEIGHT } from '../../../../dashboard/lib/resolveHomeDashboardChartHeight';
import {
	buildStatisticsDashboardChartPreviewSlotStyle,
	buildStatisticsDashboardChartTallPreviewSlotStyle,
	getPersistedChartColSpanLabel,
	getPersistedChartRowSpanLabel,
	resolvePersistedChartIsMapPreview,
	STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT,
} from '../lib/statisticsPersistedCharts';
import { isFrozenChartSnapshot, resolveDashboardChartBindingLabel } from '../lib/statisticsChartRefreshMode';
import DashboardChartRefreshModeBadge from './DashboardChartRefreshModeBadge';
import { useStatisticsContext } from '../../../../../providers/StatisticsProvider';
import { useOptionalStatisticsStatsState } from '../../../providers/StatisticsPageProvider';
import { canDuplicateChartSnapshot, canFreezeChartSnapshot, buildBuiltinLiveFreezeBundle, rendersAsBuiltinLiveChart } from '../lib/dashboardChartLifecycle';
import { refreshFrozenChartBundle } from '../lib/refreshFrozenChartBundle';
import DashboardChartExpandModal from './DashboardChartExpandModal';
import DashboardChartView from './DashboardChartView';
import useDashboardChartBundle from '../hooks/useDashboardChartBundle';
import StatisticsChartCard from '../../../common/statistic-chart-card/StatisticsChartCard.jsx';
import HomeDashboardChartLoading from '../../../../dashboard/HomeDashboardChartLoading';

const STATISTICS_CHART_CARD_MENU_POPPER_CONFIG = {
	strategy: 'absolute',
	modifiers: [{ name: 'flip', enabled: false }],
};

const isHomeChartBundleLoading = (bundle) => {
	if (!bundle) return true;
	return Boolean(
		bundle.chartLoading || bundle.trendChartLoading || bundle.isStatisticsQueryLoading || bundle.isStatisticsQueryFetching,
	);
};

const previewHasPaintedCanvas = (root) => {
	if (!root) return false;
	const canvas = root.querySelector('canvas');
	return Boolean(canvas && canvas.width > 0 && canvas.height > 0);
};

const DashboardChartCard = ({
	snapshot,
	chartBundle: chartBundleFromParent = null,
	useBatchBundle = false,
	onDelete,
	onRename,
	onDuplicate,
	canDuplicate = true,
	onRefreshSnapshot,
	onFreeze,
	onThaw,
	isManager = false,
	draggable = false,
	isDragging = false,
	isDragTarget = false,
	onDragHandleStart,
	onDragHandleEnd,
	sortableHandleRef,
	sortableHandleListeners,
	sortableHandleAttributes,
	masonryTile = 'compact',
	colSpan = 1,
	nextColSpan = 1,
	rowSpan = 1,
	nextRowSpan = 1,
	gridColumns = 4,
	canResizeWidth = false,
	canResizeHeight = false,
	onCycleWidth,
	onCycleHeight,
	onResizePointerDown,
	surface = 'statistics',
	onFullscreen,
	useSharedFullscreenModal = false,
	onPinToHome,
	pinToHomeDisabled = false,
	pinToHomeTitle = '固定口径到首页看板',
	pinnedToHome = false,
	onEdit,
}) => {
	const isDashboardSurface = surface === 'dashboard';
	const chartCardSurface = isDashboardSurface ? CHART_CARD_SURFACE.HOME : CHART_CARD_SURFACE.SNAPSHOT;
	const boardClass = getBoardChartCardClass(chartCardSurface);
	const canSortableDrag = Boolean(sortableHandleListeners);
	const canHtmlDrag = draggable && !canSortableDrag;
	const dispatch = useDispatch();
	const { overdueData } = useStatisticsContext();
	const pageStatsState = useOptionalStatisticsStatsState();
	const [expanded, setExpanded] = useState(false);
	const [isRenaming, setIsRenaming] = useState(false);
	const [renameValue, setRenameValue] = useState(snapshot.title);
	const [renameError, setRenameError] = useState('');
	const renameInputRef = useRef(null);
	const previewRef = useRef(null);
	const [fetchEnabled, setFetchEnabled] = useState(useBatchBundle || chartBundleFromParent != null);
	const [isRefreshingSnapshot, setIsRefreshingSnapshot] = useState(false);
	const [isFreezing, setIsFreezing] = useState(false);
	const [isThawing, setIsThawing] = useState(false);
	const isFrozenSnapshot = isFrozenChartSnapshot(snapshot);
	const chartBindingLabel = useMemo(() => resolveDashboardChartBindingLabel(snapshot), [snapshot]);
	const canFreeze = Boolean(onFreeze) && canFreezeChartSnapshot(snapshot);
	const canDuplicateSnapshot = Boolean(onDuplicate) && canDuplicateChartSnapshot(snapshot);
	const shouldUseParentBundle = chartBundleFromParent != null;
	const { chartBundle: chartBundleFromHook } = useDashboardChartBundle(snapshot, {
		isManager,
		skip: shouldUseParentBundle || useBatchBundle || !fetchEnabled,
	});
	const chartBundle = chartBundleFromParent ?? chartBundleFromHook;
	const homeBundleLoading = isDashboardSurface && isHomeChartBundleLoading(chartBundle);
	const [homeChartPainted, setHomeChartPainted] = useState(false);

	useEffect(() => {
		if (!isDashboardSurface) return undefined;
		if (homeBundleLoading) {
			setHomeChartPainted(false);
			return undefined;
		}

		const root = previewRef.current;
		if (!root) return undefined;
		if (previewHasPaintedCanvas(root)) {
			setHomeChartPainted(true);
			return undefined;
		}

		setHomeChartPainted(false);
		let cancelled = false;
		let rafId = 0;
		let pollId = 0;
		let failsafeId = 0;
		let observer = null;

		const stopWatching = () => {
			observer?.disconnect();
			if (pollId) window.clearInterval(pollId);
			if (failsafeId) window.clearTimeout(failsafeId);
			if (rafId) cancelAnimationFrame(rafId);
			pollId = 0;
			failsafeId = 0;
			rafId = 0;
		};
		const markPainted = () => {
			if (cancelled) return false;
			if (previewHasPaintedCanvas(root)) {
				setHomeChartPainted(true);
				stopWatching();
				return true;
			}
			return false;
		};

		observer = new MutationObserver(() => {
			markPainted();
		});
		observer.observe(root, { childList: true, subtree: true, attributes: true });
		rafId = requestAnimationFrame(() => {
			markPainted();
		});
		// MutationObserver 偶发漏掉 canvas 尺寸写入；轻量轮询补一次
		pollId = window.setInterval(() => {
			markPainted();
		}, 100);
		failsafeId = window.setTimeout(() => {
			if (!cancelled) setHomeChartPainted(true);
			stopWatching();
		}, 1400);

		return () => {
			cancelled = true;
			stopWatching();
		};
	}, [homeBundleLoading, isDashboardSurface, snapshot.id, chartBundle?.animationKey]);

	const showHomePaintSkeleton = isDashboardSurface && (homeBundleLoading || !homeChartPainted);

	useEffect(() => {
		if (useBatchBundle || chartBundleFromParent != null) return undefined;
		const node = previewRef.current;
		if (!node || fetchEnabled) return undefined;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setFetchEnabled(true);
				}
			},
			{ rootMargin: '160px 0px', threshold: 0.01 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [chartBundleFromParent, fetchEnabled, useBatchBundle]);

	const openExpanded = useCallback(() => {
		if (useSharedFullscreenModal && onFullscreen) {
			onFullscreen(snapshot.id);
			return;
		}
		setFetchEnabled(true);
		setExpanded(true);
	}, [onFullscreen, snapshot.id, useSharedFullscreenModal]);
	const closeExpanded = useCallback(() => setExpanded(false), []);

	useEffect(() => {
		if (!isRenaming) {
			setRenameValue(snapshot.title);
			setRenameError('');
		}
	}, [isRenaming, snapshot.title]);

	useEffect(() => {
		if (isRenaming) {
			renameInputRef.current?.focus();
			renameInputRef.current?.select();
		}
	}, [isRenaming]);

	const isMapPreview = resolvePersistedChartIsMapPreview(snapshot, chartBundle);
	const isTallPreview = !isDashboardSurface && rowSpan >= 2;
	const previewHeight = isDashboardSurface ? HOME_DASHBOARD_CHART_PREVIEW_HEIGHT : isTallPreview ? null : STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT;
	const previewSlotStyle = isDashboardSurface
		? buildHomeDashboardChartPreviewSlotStyle(previewHeight)
		: isTallPreview
			? buildStatisticsDashboardChartTallPreviewSlotStyle()
			: buildStatisticsDashboardChartPreviewSlotStyle(previewHeight);

	useEffect(() => {
		if (isDashboardSurface) return undefined;
		const frameId = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
		return () => cancelAnimationFrame(frameId);
	}, [isDashboardSurface, previewHeight, rowSpan]);

	const startRename = useCallback(() => {
		setRenameValue(snapshot.title);
		setRenameError('');
		setIsRenaming(true);
	}, [snapshot.title]);

	const cancelRename = useCallback(() => {
		setRenameValue(snapshot.title);
		setRenameError('');
		setIsRenaming(false);
	}, [snapshot.title]);

	const submitRename = useCallback(() => {
		if (!onRename) return;
		const result = onRename(snapshot.id, renameValue);
		if (result?.renamed) {
			setIsRenaming(false);
			setRenameError('');
			return;
		}
		if (result?.reason === 'empty') {
			setRenameError('名称不能为空');
		}
	}, [onRename, renameValue, snapshot.id]);

	const handleRenameKeyDown = useCallback(
		(event) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				submitRename();
			}
			if (event.key === 'Escape') {
				event.preventDefault();
				cancelRename();
			}
		},
		[cancelRename, submitRename],
	);

	const handleTitleDoubleClick = useCallback(() => {
		if (!onRename) return;
		startRename();
	}, [onRename, startRename]);

	const handleRenameBlur = useCallback(() => {
		if (renameValue.trim() === snapshot.title.trim()) {
			cancelRename();
			return;
		}
		submitRename();
	}, [cancelRename, renameValue, snapshot.title, submitRename]);

	const handleDuplicate = useCallback(() => {
		onDuplicate?.(snapshot.id);
	}, [onDuplicate, snapshot.id]);

	const handleRefreshSnapshot = useCallback(async () => {
		if (!onRefreshSnapshot || !isFrozenSnapshot || isRefreshingSnapshot) return;
		setIsRefreshingSnapshot(true);
		try {
			const nextBundle = await refreshFrozenChartBundle({
				dispatch,
				snapshot,
				overdueData,
				pageStatsState,
				isManager,
			});
			if (nextBundle) {
				onRefreshSnapshot(snapshot.id, nextBundle);
			}
		} finally {
			setIsRefreshingSnapshot(false);
		}
	}, [dispatch, isFrozenSnapshot, isManager, isRefreshingSnapshot, onRefreshSnapshot, overdueData, pageStatsState, snapshot]);

	const handleFreeze = useCallback(async () => {
		if (!onFreeze || isFreezing) return;
		setIsFreezing(true);
		const freezeBundle = rendersAsBuiltinLiveChart(snapshot) ? buildBuiltinLiveFreezeBundle({ snapshot, statsState: pageStatsState, overdueData }) : chartBundle;
		await onFreeze(snapshot.id, freezeBundle);
		setIsFreezing(false);
	}, [chartBundle, isFreezing, onFreeze, overdueData, pageStatsState, snapshot]);

	const handleThaw = useCallback(async () => {
		if (!onThaw || isThawing) return;
		setIsThawing(true);
		await onThaw(snapshot.id);
		setIsThawing(false);
	}, [isThawing, onThaw, snapshot.id]);

	const handleCycleWidth = useCallback(() => {
		onCycleWidth?.();
	}, [onCycleWidth]);

	const handlePinToHome = useCallback(() => {
		onPinToHome?.(snapshot, chartBundle);
	}, [chartBundle, onPinToHome, snapshot]);

	const handleEdit = useCallback(() => {
		onEdit?.(snapshot);
	}, [onEdit, snapshot]);

	const preventRenameBlur = useCallback((event) => {
		event.preventDefault();
	}, []);

	const titleNode = isRenaming ? (
		<div className='statistics-persisted-chart-card__rename'>
			<input
				ref={renameInputRef}
				type='text'
				className='statistics-persisted-chart-card__rename-input'
				value={renameValue}
				maxLength={96}
				onChange={(event) => setRenameValue(event.target.value)}
				onKeyDown={handleRenameKeyDown}
				onBlur={handleRenameBlur}
				aria-label='图表名称'
			/>
			<div className='statistics-persisted-chart-card__rename-actions'>
				<button type='button' className='statistics-persisted-chart-card__rename-btn' onMouseDown={preventRenameBlur} onClick={submitRename}>
					保存
				</button>
				<button
					type='button'
					className='statistics-persisted-chart-card__rename-btn statistics-persisted-chart-card__rename-btn--ghost'
					onMouseDown={preventRenameBlur}
					onClick={cancelRename}
				>
					取消
				</button>
			</div>
			{renameError ? <span className='statistics-persisted-chart-card__rename-error'>{renameError}</span> : null}
		</div>
	) : (
		<div className='dashboard-chart-card__title-group'>
			{isDashboardSurface ? (
				<h6
					className={classNames(boardClass.title, { 'is-editable': onRename })}
					title={onRename ? `${snapshot.title}（双击修改名称）` : snapshot.title}
					onDoubleClick={handleTitleDoubleClick}
				>
					{snapshot.title}
				</h6>
			) : (
				<h4
					className={classNames(boardClass.title, { 'is-editable': onRename })}
					title={onRename ? `${snapshot.title}（双击修改名称）` : snapshot.title}
					onDoubleClick={handleTitleDoubleClick}
				>
					{snapshot.title}
				</h4>
			)}
			<DashboardChartRefreshModeBadge snapshot={snapshot} />
		</div>
	);

	const toolbarNode = !isRenaming ? (
		isDashboardSurface ? (
			<StatisticsChartCardToolbar surface={chartCardSurface}>
				{canResizeWidth ? (
					<StatisticsChartCardAction
						surface={chartCardSurface}
						kind='icon'
						onClick={handleCycleWidth}
						title={`当前${getPersistedChartColSpanLabel(colSpan, gridColumns)}，点击切换为${getPersistedChartColSpanLabel(nextColSpan, gridColumns)}`}
					>
						<StatisticsWidthIcon size={15} colSpan={colSpan} />
					</StatisticsChartCardAction>
				) : null}
				<CardDropdown drop='down' align='end' btnRevealClass='statistics-chart-card__menu-trigger'>
					{onEdit ? (
						<Dropdown.Item as='button' type='button' className='w-100 text-start' onClick={handleEdit}>
							编辑
						</Dropdown.Item>
					) : null}
					<Dropdown.Item as='button' type='button' className='w-100 text-start text-danger' onClick={() => onDelete(snapshot.id)}>
						从首页移除
					</Dropdown.Item>
				</CardDropdown>
			</StatisticsChartCardToolbar>
		) : (
			<StatisticsChartCardToolbar surface={chartCardSurface}>
				{(isFrozenSnapshot && onRefreshSnapshot) || (isFrozenSnapshot && onThaw) || (!isFrozenSnapshot && canFreeze) || onPinToHome || onEdit ? (
					<CardDropdown
						drop='down'
						align='end'
						btnRevealClass='statistics-chart-card__menu-trigger'
						boundary='clippingParents'
						popperConfig={STATISTICS_CHART_CARD_MENU_POPPER_CONFIG}
					>
						{isFrozenSnapshot && onRefreshSnapshot ? (
							<Dropdown.Item
								as='button'
								type='button'
								className='w-100 text-start'
								onClick={handleRefreshSnapshot}
								disabled={isRefreshingSnapshot}
								title='按原口径重新抓取并更新快照'
							>
								{isRefreshingSnapshot ? '刷新中…' : '刷新快照'}
							</Dropdown.Item>
						) : null}
						{isFrozenSnapshot && onThaw ? (
							<Dropdown.Item
								as='button'
								type='button'
								className='w-100 text-start'
								onClick={handleThaw}
								disabled={isThawing}
								title={`恢复为${chartBindingLabel.short}实时刷新，不再使用定格快照`}
							>
								{isThawing ? '恢复中…' : '恢复实时'}
							</Dropdown.Item>
						) : null}
						{!isFrozenSnapshot && canFreeze ? (
							<Dropdown.Item as='button' type='button' className='w-100 text-start' onClick={handleFreeze} disabled={isFreezing} title='将当前数据定格为快照，数字不再实时变化'>
								{isFreezing ? '定格中…' : '定格快照'}
							</Dropdown.Item>
						) : null}
						{onPinToHome ? (
							<Dropdown.Item as='button' type='button' className='w-100 text-start' onClick={handlePinToHome} disabled={pinToHomeDisabled} title={pinToHomeTitle}>
								{pinnedToHome ? '已在首页' : '固定至首页'}
							</Dropdown.Item>
						) : null}
						{onEdit ? (
							<Dropdown.Item as='button' type='button' className='w-100 text-start' onClick={handleEdit} title='编辑图表配置'>
								编辑
							</Dropdown.Item>
						) : null}
					</CardDropdown>
				) : null}
				{canResizeHeight ? (
					<StatisticsChartCardAction
						surface={chartCardSurface}
						kind='icon'
						onClick={onCycleHeight}
						title={`当前${getPersistedChartRowSpanLabel(rowSpan)}，点击切换为${getPersistedChartRowSpanLabel(nextRowSpan)}`}
					>
						<StatisticsHeightIcon size={17} rowSpan={rowSpan} />
					</StatisticsChartCardAction>
				) : null}
				{canResizeWidth ? (
					<StatisticsChartCardAction
						surface={chartCardSurface}
						kind='icon'
						onClick={handleCycleWidth}
						title={`当前${getPersistedChartColSpanLabel(colSpan, gridColumns)}，点击切换为${getPersistedChartColSpanLabel(nextColSpan, gridColumns)}`}
					>
						<StatisticsWidthIcon size={17} colSpan={colSpan} />
					</StatisticsChartCardAction>
				) : null}
				{canDuplicateSnapshot ? (
					<StatisticsChartCardAction
						surface={chartCardSurface}
						kind='icon'
						onClick={handleDuplicate}
						disabled={!canDuplicate}
						title={!canDuplicate ? '已达固定上限' : '复制'}
					>
						<StatisticsCopyIcon size={17} />
					</StatisticsChartCardAction>
				) : null}
				<StatisticsChartCardFullscreenAction surface={chartCardSurface} onClick={openExpanded} title='查看原尺寸' />
				{onDelete ? (
					<StatisticsChartCardAction surface={chartCardSurface} kind='danger' onClick={() => onDelete(snapshot.id)} title={`删除 ${snapshot.title}`}>
						<StatisticsDeleteIcon size={17} />
					</StatisticsChartCardAction>
				) : null}
			</StatisticsChartCardToolbar>
		)
	) : null;

	const previewNode = (
		<div
			ref={previewRef}
			className={classNames(
				boardClass.preview,
				isDashboardSurface ? boardClass.previewFixed : isTallPreview ? boardClass.previewTall : boardClass.previewFixed,
				isDashboardSurface && isMapPreview ? boardClass.previewMap : null,
				showHomePaintSkeleton ? 'dashboard-home-chart-card__preview--paint-gate' : null,
			)}
			style={previewSlotStyle}
		>
			{showHomePaintSkeleton ? (
				<div className='statistics-chart-preview-paint-gate' aria-hidden='true'>
					<HomeDashboardChartLoading label='加载图表' />
				</div>
			) : null}
			{isDashboardSurface ? (
				<div
					className='dashboard-home-chart-card__preview-chart'
					style={showHomePaintSkeleton ? { visibility: 'hidden' } : undefined}
					aria-hidden={showHomePaintSkeleton || undefined}
				>
					<DashboardChartView
						snapshot={snapshot}
						chartBundle={chartBundle}
						height={previewHeight}
						previewHeight={previewHeight}
						showPointLabels={false}
						fillHeight
						fillPreview
					/>
				</div>
			) : (
				<DashboardChartView
					snapshot={snapshot}
					chartBundle={chartBundle}
					height={previewHeight}
					previewHeight={previewHeight}
					showPointLabels={false}
					fillHeight
					fillPreview
				/>
			)}
		</div>
	);

	const canDragHead = isDashboardSurface ? (canHtmlDrag || canSortableDrag) && !isRenaming : draggable && !isRenaming;

	const headMainProps = {
		...(isDashboardSurface
			? {
					ref: sortableHandleRef,
					...(canSortableDrag && !isRenaming ? sortableHandleListeners : {}),
					...(canSortableDrag && !isRenaming ? sortableHandleAttributes : {}),
				}
			: {}),
		className: classNames({
			[boardClass.headMainDraggable]: canDragHead,
			'is-dragging': isDragging,
		}),
		draggable: (isDashboardSurface ? canHtmlDrag && !isRenaming : canDragHead) || undefined,
		onDragStart: (isDashboardSurface ? canHtmlDrag && !isRenaming : canDragHead) ? onDragHandleStart : undefined,
		onDragEnd: (isDashboardSurface ? canHtmlDrag && !isRenaming : canDragHead) ? onDragHandleEnd : undefined,
		title: canDragHead ? '按住标题拖拽排序' : undefined,
	};

	return (
		<>
			<StatisticsChartCard
				surface={chartCardSurface}
				isDragging={isDragging}
				isDragTarget={isDragTarget}
				isMapPreview={isDashboardSurface && isMapPreview}
				className={!isDashboardSurface && rowSpan >= 2 ? 'statistics-persisted-chart-card--rows-2' : undefined}
				meta={titleNode}
				toolbar={toolbarNode}
				headMainProps={headMainProps}
				canResizeWidth={canResizeWidth}
				onResizePointerDown={onResizePointerDown}
			>
				{previewNode}
			</StatisticsChartCard>
			{!useSharedFullscreenModal ? <DashboardChartExpandModal show={expanded} onHide={closeExpanded} snapshot={snapshot} chartBundle={chartBundle} isManager={isManager} /> : null}
		</>
	);
};

DashboardChartCard.propTypes = {
	snapshot: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		selectedPhase: PropTypes.string,
		ui: PropTypes.object,
	}).isRequired,
	chartBundle: PropTypes.object,
	useBatchBundle: PropTypes.bool,
	onDelete: PropTypes.func,
	onRename: PropTypes.func,
	onDuplicate: PropTypes.func,
	onRefreshSnapshot: PropTypes.func,
	onFreeze: PropTypes.func,
	onThaw: PropTypes.func,
	canDuplicate: PropTypes.bool,
	isManager: PropTypes.bool,
	draggable: PropTypes.bool,
	isDragging: PropTypes.bool,
	isDragTarget: PropTypes.bool,
	onDragHandleStart: PropTypes.func,
	onDragHandleEnd: PropTypes.func,
	sortableHandleRef: PropTypes.func,
	sortableHandleListeners: PropTypes.object,
	sortableHandleAttributes: PropTypes.object,
	masonryTile: PropTypes.oneOf(['compact', 'tall', 'hero', 'featured', 'wide']),
	colSpan: PropTypes.oneOf([1, 2, 3, 4]),
	nextColSpan: PropTypes.oneOf([1, 2, 3, 4]),
	rowSpan: PropTypes.oneOf([1, 2]),
	nextRowSpan: PropTypes.oneOf([1, 2]),
	gridColumns: PropTypes.oneOf([1, 2, 3, 4]),
	canResizeWidth: PropTypes.bool,
	canResizeHeight: PropTypes.bool,
	onCycleWidth: PropTypes.func,
	onCycleHeight: PropTypes.func,
	onResizePointerDown: PropTypes.func,
	surface: PropTypes.oneOf(['statistics', 'dashboard']),
	onFullscreen: PropTypes.func,
	useSharedFullscreenModal: PropTypes.bool,
	onPinToHome: PropTypes.func,
	pinToHomeDisabled: PropTypes.bool,
	pinToHomeTitle: PropTypes.string,
	pinnedToHome: PropTypes.bool,
	onEdit: PropTypes.func,
};

export default memo(DashboardChartCard);
